import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Blog post not found</p>
          <Link to="/blog">
            <Button>Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/blog">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft size={16} className="mr-2" /> Back to Blog
          </Button>
        </Link>

        {blog.image_url && (
          <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
            <img src={blog.image_url} alt={blog.title} className="w-full h-96 object-cover" />
          </div>
        )}

        <article className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-4xl font-bold text-[#0D2847] mb-4">{blog.title}</h1>
          
          <div className="flex items-center text-gray-500 mb-8 pb-8 border-b">
            <User size={16} className="mr-2" />
            <span className="mr-6">{blog.author}</span>
            <Calendar size={16} className="mr-2" />
            <span>{new Date(blog.date).toLocaleDateString()}</span>
          </div>

          <div className="prose prose-lg max-w-none mb-12">
            {blog.content.split('\n').map((paragraph, index) => (
              <p key={index} className="mb-4 text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>

          {blog.studies && blog.studies.length > 0 && (
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-[#0D2847] mb-6">Related Scientific Studies</h2>
              <div className="grid grid-cols-1 gap-4">
                {blog.studies.map((study, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-lg">{study.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm mb-3">{study.summary}</p>
                      <a 
                        href={study.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#0D2847] hover:underline text-sm font-medium flex items-center"
                      >
                        Read Full Study <ExternalLink size={14} className="ml-1" />
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>
    </div>
  );
};

export default BlogPost;
